import { useState } from 'react'
import {
  useCreateTagMutation,
  type TagSourceTypeName
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select'

interface FormState {
  tagPath: string
  sourceUrl: string
  sourceType: TagSourceTypeName
  displayLabel: string
  frequencyMs: number
}

const DEFAULT_FORM: FormState = {
  tagPath: '',
  sourceUrl: '',
  sourceType: 'ignition',
  displayLabel: '',
  frequencyMs: 5000
}

const SOURCE_TYPE_OPTIONS: ReadonlyArray<{
  value: TagSourceTypeName
  label: string
}> = [
  { value: 'ignition', label: 'Ignition' },
  { value: 'opcua', label: 'OPC-UA' },
  { value: 'opcda', label: 'OPC-DA' }
]

export function TagAddForm({ onCreated }: { onCreated?: () => void }) {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [createTag] = useCreateTagMutation()

  const reset = () => {
    setForm(DEFAULT_FORM)
    setError(null)
    setSubmitting(false)
  }

  const handleSubmit = async () => {
    if (!form.tagPath.trim() || !form.sourceUrl.trim()) {
      setError('tag_path and source_url are required.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await createTag({
        tag: {
          tagPath: form.tagPath.trim(),
          sourceUrl: form.sourceUrl.trim(),
          sourceType: form.sourceType,
          displayLabel: form.displayLabel.trim() || undefined,
          state: 'enabled'
        },
        initialScrapeConfig: { frequencyMs: form.frequencyMs }
      })
      reset()
      setNotice('Tag created.')
      onCreated?.()
    } catch (err) {
      setError(String(err))
      setSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="tag-add-path">
          Tag path <span className="text-st-down">*</span>
        </Label>
        <Input
          id="tag-add-path"
          aria-label="Tag path"
          value={form.tagPath}
          placeholder="My/Plant/Boiler1/Temperature"
          onChange={(e) => {
            setNotice(null)
            setForm({ ...form, tagPath: e.target.value })
          }}
          disabled={submitting}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="tag-add-source-url">
          Source URL <span className="text-st-down">*</span>
        </Label>
        <Input
          id="tag-add-source-url"
          aria-label="Source URL"
          value={form.sourceUrl}
          placeholder="http://gateway.example.com:8088"
          onChange={(e) => {
            setNotice(null)
            setForm({ ...form, sourceUrl: e.target.value })
          }}
          disabled={submitting}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="tag-add-source-type">Source type</Label>
        <Select
          value={form.sourceType}
          onValueChange={(value) => {
            if (value) setForm({ ...form, sourceType: value as TagSourceTypeName })
          }}
          disabled={submitting}
        >
          <SelectTrigger id="tag-add-source-type" aria-label="Source type" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SOURCE_TYPE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="tag-add-display-label">Display label (optional)</Label>
        <Input
          id="tag-add-display-label"
          aria-label="Display label"
          value={form.displayLabel}
          placeholder="Boiler 1 temperature"
          onChange={(e) => setForm({ ...form, displayLabel: e.target.value })}
          disabled={submitting}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="tag-add-frequency">Frequency (ms)</Label>
        <Input
          id="tag-add-frequency"
          type="number"
          aria-label="Frequency in milliseconds"
          min={100}
          step={100}
          value={form.frequencyMs}
          onChange={(e) => setForm({ ...form, frequencyMs: Number(e.target.value) || 0 })}
          disabled={submitting}
        />
      </div>

      {error ? (
        <div className="rounded-md bg-st-down-bg p-2 text-sm text-st-down">{error}</div>
      ) : null}
      {notice ? (
        <div className="rounded-md bg-st-ok-bg p-2 text-sm text-st-ok" role="status">
          {notice}
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? 'Creating…' : 'Create tag'}
        </Button>
      </div>
    </div>
  )
}
